import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Clock, X, ArrowRight } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const KEY = 'ivoliq_free_timer_v1';
const DISMISS = 'ivoliq_free_timer_reminder_dismissed';
const HIDE_ON = ['/login', '/signup', '/zeiterfassung-freelancer', '/lex', '/advisor', '/admin'];

const fmtDur = (sec) => (sec < 3600 ? `${Math.max(1, Math.round(sec / 60))} Min.` : `${(sec / 3600).toFixed(1).replace('.', ',')} h`);

/** Dezenter Hinweis für Besucher mit ungespeicherten Einträgen aus dem kostenlosen Browser-Timer. */
export default function TimerReminder() {
  const { isAuthenticated } = useAuth();
  const { pathname } = useLocation();
  const [state, setState] = useState(null);
  const [hidden, setHidden] = useState(() => sessionStorage.getItem(DISMISS) === '1');
  const [cookieDecided, setCookieDecided] = useState(() => !!localStorage.getItem('ivoliq_cookie_consent'));

  useEffect(() => {
    if (cookieDecided) return undefined;
    const t = setInterval(() => { if (localStorage.getItem('ivoliq_cookie_consent')) setCookieDecided(true); }, 1000);
    return () => clearInterval(t);
  }, [cookieDecided]);

  useEffect(() => {
    try {
      const s = JSON.parse(localStorage.getItem(KEY));
      const entries = (s?.entries || []).filter(e => e.seconds > 0);
      setState(entries.length ? { count: entries.length, seconds: entries.reduce((a, e) => a + e.seconds, 0) } : null);
    } catch { setState(null); }
  }, [pathname]);

  if (isAuthenticated || hidden || !state || !cookieDecided || HIDE_ON.some(p => pathname.startsWith(p))) return null;

  const dismiss = () => { sessionStorage.setItem(DISMISS, '1'); setHidden(true); };
  const goSignup = () => { try { localStorage.setItem('post_auth_redirect', '/time-tracking'); } catch { /* ignore */ } };

  return (
    <div role="status" data-testid="timer-reminder" className="fixed right-4 bottom-24 md:bottom-4 z-40 max-w-sm rounded-2xl bg-white p-4 pr-10 shadow-2xl ring-1 ring-black/10 animate-in fade-in slide-in-from-bottom-4">
      <button type="button" onClick={dismiss} aria-label="Hinweis schließen" data-testid="timer-reminder-dismiss" className="absolute right-2 top-2 rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"><X className="h-4 w-4" /></button>
      <div className="flex items-start gap-3">
        <span className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-emerald-100 text-emerald-700"><Clock className="h-4 w-4" /></span>
        <div className="text-sm">
          <p className="font-semibold text-gray-900" data-testid="timer-reminder-text">{state.count} {state.count === 1 ? 'Eintrag wartet' : 'Einträge warten'} ({fmtDur(state.seconds)}) – noch nicht gesichert</p>
          <p className="mt-0.5 text-gray-600">Aus deinem kostenlosen Timer. Mit einem Konto werden sie automatisch übernommen.</p>
          <div className="mt-2 flex items-center gap-3">
            <Link to="/signup" onClick={goSignup} data-testid="timer-reminder-signup" className="inline-flex items-center gap-1 font-semibold text-emerald-700 hover:underline underline-offset-4">Jetzt Konto anlegen <ArrowRight className="h-3.5 w-3.5" /></Link>
            <Link to="/zeiterfassung-freelancer" data-testid="timer-reminder-open" className="text-gray-500 hover:underline underline-offset-4">Zum Timer</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
